import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowDown, Cpu } from 'lucide-react';
import { CalibrationInterface } from './CalibrationInterface';

interface DiscoveryHeroProps {
  onScrollToForm: () => void;
}

export const DiscoveryHero: React.FC<DiscoveryHeroProps> = ({ onScrollToForm }) => {
  return (
    <section className="relative h-screen min-h-[600px] max-h-[900px] flex items-center px-4 sm:px-6 lg:px-8 pt-20 sm:pt-24 pb-8 sm:pb-12 overflow-hidden">
      <div className="container mx-auto max-w-7xl relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">

          {/* Left Column: Content */}
          <div className="order-2 md:order-1 text-center md:text-left">
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center px-4 py-2 bg-luxury-gold/10 border border-luxury-gold/20 rounded-full mb-6"
            >
              <Cpu className="w-4 h-4 text-luxury-gold mr-2" />
              <span className="text-sm font-medium text-luxury-gold">Discovery Protocol</span>
            </motion.div>

            {/* Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="text-[22px] sm:text-[28px] md:text-4xl lg:text-5xl xl:text-6xl font-bold tracking-tight leading-tight mb-6"
            >
              <span className="text-luxury-text dark:text-luxury-text-dark">
                Your Investment Criteria.
              </span>
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-luxury-gold via-luxury-gold-light to-luxury-gold">
                Our Execution Infrastructure.
              </span>
            </motion.h1>

            {/* Subline */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-sm sm:text-base md:text-lg text-luxury-text-muted dark:text-luxury-text-muted-dark leading-relaxed max-w-xl mx-auto md:mx-0 mb-6 sm:mb-8"
            >
              30 minutes to map your goals. If there's fit, we deploy custom algorithms
              to proactively source deals aligned with your requirements.
            </motion.p>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="flex flex-row items-center justify-center md:justify-start gap-3 sm:gap-4"
            >
              {/* Primary CTA */}
              <button
                onClick={onScrollToForm}
                className="group relative inline-flex items-center justify-center px-4 sm:px-7 py-3 sm:py-4 text-xs sm:text-base font-semibold text-luxury-text tracking-wide transition-all duration-300 bg-gradient-to-r from-luxury-gold to-luxury-gold-light rounded-full hover:shadow-gold-intense hover:-translate-y-1 shadow-gold-lg overflow-hidden"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000" />
                <span className="relative flex items-center">
                  Schedule Call
                  <ArrowDown className="ml-1.5 sm:ml-2 w-3.5 h-3.5 sm:w-5 sm:h-5 group-hover:translate-y-1 transition-transform" />
                </span>
              </button>

              {/* Secondary CTA */}
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-4 sm:px-7 py-3 sm:py-4 text-xs sm:text-base font-medium text-luxury-gold transition-all duration-300 bg-transparent border-2 border-luxury-gold/30 rounded-full hover:bg-luxury-gold/5 hover:border-luxury-gold/50"
              >
                How It Works
                <ArrowRight className="ml-1.5 sm:ml-2 w-3.5 h-3.5 sm:w-4 sm:h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>

            {/* Trust Line */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="mt-6 text-sm text-luxury-text-muted dark:text-luxury-text-muted-dark"
            >
              Google Meet or phone · 30 minutes · No obligation
            </motion.p>
          </div>

          {/* Right Column: Calibration Interface Graphic */}
          <div className="order-1 md:order-2 flex justify-center md:justify-end">
            <CalibrationInterface />
          </div>
        </div>
      </div>
    </section>
  );
};

export default DiscoveryHero;
